import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { App } from '@capacitor/app';

import { useAuthContext } from '@/domains/auth/authContext';
import { useBiometrics } from '@/domains/shared/components/biometrics/BiometricsContext';
import { useIdentityBoot } from '@/domains/shared/identity/IdentityBootContext';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';

import {
  INITIAL_UNLOCK_STATE,
  unlockReducer,
  type UnlockAction,
  type UnlockContext,
  type UnlockState,
} from './unlockMachine';
import { isNative } from '@/domains/shared/utils/native';

const LOG_PREFIX = '[UNLOCK_MACHINE]';

/**
 * Re-lock the app if it was backgrounded for longer than this. Matches the
 * existing PinManager behaviour pre-refactor.
 */
const BACKGROUND_LOCK_TIMEOUT_MS = 60 * 1000;

/**
 * Console logs are styled with %c so they jump out in the console when
 * debugging native builds via remote inspect. Disabled in production.
 */
const IS_DEBUGGABLE = process.env.NEXT_PUBLIC_PLATFORM_APP_ENV !== 'production';

const CONSOLE_BADGE =
  'background:#1976d2;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;';
const CONSOLE_FROM = 'color:#888;text-decoration:line-through;';
const CONSOLE_ARROW = 'color:#666;font-weight:bold;';
const CONSOLE_TO = 'color:#1976d2;font-weight:bold;';
const CONSOLE_ACTION = 'color:#1976d2;font-weight:bold;';
const CONSOLE_RESET = 'color:inherit;';

interface UnlockMachineHandle {
  state: UnlockState;
  actions: {
    pinCorrect: () => void;
    pinWrong: () => void;
    pinSetupCompleted: () => void;
  };
}

/**
 * Drives the unlock state machine in response to the data layer
 * (IdentityBoot, AuthContext, Biometrics) and the app lifecycle. The
 * reducer in `unlockMachine.ts` decides all transitions; this hook only
 * dispatches actions and runs the side effects each state implies
 * (call `authenticate()`, call `logout()`, etc.).
 */
export const useUnlockMachine = (): UnlockMachineHandle => {
  const { isLoggedIn, logout } = useAuthContext();
  const boot = useIdentityBoot();
  const biometrics = useBiometrics();

  const [state, dispatchRaw] = useReducer(unlockReducer, INITIAL_UNLOCK_STATE);

  // Centralised dispatch with logging so every transition shows up in
  // Datadog. If a future cold-boot bug stalls the machine in one state,
  // we'll see exactly which action did or did not fire.
  const dispatch = useCallback((action: UnlockAction) => {
    logToDatadog('info', `${LOG_PREFIX} dispatch ${action.type}`, {
      action: action.type,
    });
    if (IS_DEBUGGABLE) {
      console.log(
        `%c UNLOCK %c dispatch %c${action.type}`,
        CONSOLE_BADGE,
        CONSOLE_RESET,
        CONSOLE_ACTION,
        action.type === 'EVALUATE' ? action.ctx : '',
      );
    }
    dispatchRaw(action);
  }, []);

  // Visible log of every state transition. The reducer is pure so a
  // dispatch can be a no-op (e.g. a late biometric resolve when state
  // has already moved on); this effect only fires when state actually
  // changes, which is the more useful signal for debugging.
  const prevStateRef = useRef<UnlockState['kind']>(state.kind);
  useEffect(() => {
    if (prevStateRef.current === state.kind) return;
    const from = prevStateRef.current;
    const to = state.kind;
    prevStateRef.current = to;

    logToDatadog('info', `${LOG_PREFIX} state ${from} → ${to}`, { from, to });
    if (IS_DEBUGGABLE) {
      console.log(
        `%c UNLOCK %c %c${from}%c %c→%c %c${to}`,
        CONSOLE_BADGE,
        CONSOLE_RESET,
        CONSOLE_FROM,
        CONSOLE_RESET,
        CONSOLE_ARROW,
        CONSOLE_RESET,
        CONSOLE_TO,
      );
    }
  }, [state.kind]);

  const ctx: UnlockContext = useMemo(
    () => ({
      // The platform is stable per session — no need to track it as a dep.
      isNative: isNative(),
      isLoggedIn,
      pinSet: boot.pinSet,
      biometricAvailable: biometrics.available,
      biometricEnabled: biometrics.enabled,
    }),
    [isLoggedIn, boot.pinSet, biometrics.available, biometrics.enabled],
  );

  /* ─────────────────────────── transitions ─────────────────────────── */

  // 1. Initial boot evaluation. Fires exactly once when IdentityBoot
  //    reaches `ready`. Subsequent boot.pinSet / biometric changes do NOT
  //    re-fire this — they're only relevant on the next session lifecycle
  //    event (login / re-lock).
  const initialEvalDoneRef = useRef(false);
  // Tracks the isLoggedIn value the machine last reacted to. Seeded by the
  // initial EVALUATE below so effect 2 only reacts to *transitions* after
  // boot, not to its own first run where wasLoggedIn would otherwise be
  // undefined (falsy) and trigger a redundant EVALUATE on cold boot.
  const lastLoggedInRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!boot.ready) return;
    if (initialEvalDoneRef.current) return;
    initialEvalDoneRef.current = true;
    lastLoggedInRef.current = ctx.isLoggedIn;
    dispatch({ type: 'EVALUATE', ctx });
    // ctx intentionally not a dep — we want the ctx at the moment of
    // first boot.ready, not whatever it evolves to afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boot.ready, dispatch]);

  // 2. Logout / re-login. Fires whenever `isLoggedIn` flips post-boot.
  //    LOGGED_OUT / EVALUATE are idempotent but the ref guard prevents
  //    redundant dispatches when unrelated deps churn.
  useEffect(() => {
    if (!initialEvalDoneRef.current) return;
    if (lastLoggedInRef.current === isLoggedIn) return;
    const wasLoggedIn = lastLoggedInRef.current;
    lastLoggedInRef.current = isLoggedIn;

    if (wasLoggedIn && !isLoggedIn) {
      dispatch({ type: 'LOGGED_OUT' });
    } else if (!wasLoggedIn && isLoggedIn) {
      // 3. Re-login from signed-out: re-evaluate from scratch.
      dispatch({ type: 'EVALUATE', ctx });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, dispatch]);

  /* ────────────────────────── side effects ─────────────────────────── */

  // 4. When state enters `attempting-biometric`, run authenticate(). The
  //    ref guards against re-firing if the effect re-runs while the
  //    promise is still pending (e.g. unrelated dep change).
  //
  //    `promptBiometricAuth` is defensively written and returns `false`
  //    on error/timeout/cancel — but we *also* catch here because this
  //    effect has no other recovery path. If authenticate() ever throws
  //    (regression in the biometrics module, unexpected native plugin
  //    behaviour, etc.), the IIFE would never dispatch, the in-flight
  //    ref would stay true, and the machine would be stuck in
  //    `attempting-biometric` forever — a permanent white HoldingScreen.
  //    Treating a throw as a failed attempt drops us into PIN entry,
  //    which is the right user-facing fallback.
  //
  //    `useBiometrics()` returns a brand new object every render (and
  //    `authenticate` is only incidentally stable today), so we read it
  //    through a ref rather than depending on it. If `biometrics` were a
  //    dep, an unrelated rerender would re-run this effect while still in
  //    `attempting-biometric`: the cleanup would cancel the in-flight
  //    attempt while the in-flight guard blocked a fresh one, stranding
  //    the machine. Keeping the deps to `state.kind` (+ stable `dispatch`)
  //    means cleanup only ever cancels on a genuine state transition.
  const authenticateRef = useRef(biometrics.authenticate);
  authenticateRef.current = biometrics.authenticate;

  const bioAttemptInFlightRef = useRef(false);
  useEffect(() => {
    if (!isNative()) return;
    if (state.kind !== 'attempting-biometric') {
      bioAttemptInFlightRef.current = false;
      return;
    }
    if (bioAttemptInFlightRef.current) return;
    bioAttemptInFlightRef.current = true;

    let cancelled = false;
    (async () => {
      let success = false;
      try {
        success = await authenticateRef.current();
      } catch (error) {
        logToDatadog(
          'error',
          `${LOG_PREFIX} biometrics.authenticate threw — falling back to PIN entry`,
          { error: error instanceof Error ? error.message : String(error) },
        );
      }
      if (cancelled) return;
      dispatch({
        type: success ? 'BIOMETRIC_SUCCEEDED' : 'BIOMETRIC_FAILED',
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [state.kind, dispatch]);

  // 5. When state enters `logging-out`, fire logout(). Firebase will
  //    publish the auth state change, isLoggedIn will flip false, and
  //    the effect above will dispatch LOGGED_OUT — landing us in
  //    signed-out.
  const logoutInFlightRef = useRef(false);
  useEffect(() => {
    if (state.kind !== 'logging-out') {
      logoutInFlightRef.current = false;
      return;
    }
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;
    void logout().catch((error) => {
      logToDatadog(
        'error',
        `${LOG_PREFIX} logout threw during logging-out state`,
        { error: error instanceof Error ? error.message : String(error) },
      );
    });
  }, [state.kind, logout]);

  // 6. App backgrounded > 60s: re-lock. We only emit EVALUATE when the
  //    user was actually unlocked — otherwise the state machine is
  //    already showing the right unlock UI.
  const backgroundTimeRef = useRef<number | null>(null);
  const stateRef = useRef(state);
  const ctxRef = useRef(ctx);
  useEffect(() => {
    stateRef.current = state;
    ctxRef.current = ctx;
  }, [state, ctx]);

  useEffect(() => {
    if (!isNative()) return;

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        backgroundTimeRef.current = Date.now();
        return;
      }
      if (backgroundTimeRef.current === null) return;
      const timeAway = Date.now() - backgroundTimeRef.current;
      backgroundTimeRef.current = null;

      if (
        timeAway > BACKGROUND_LOCK_TIMEOUT_MS &&
        stateRef.current.kind === 'unlocked'
      ) {
        dispatch({ type: 'EVALUATE', ctx: ctxRef.current });
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [dispatch]);

  /* ────────────────────────── action API ───────────────────────────── */

  const actions = useMemo(
    () => ({
      pinCorrect: () => dispatch({ type: 'PIN_CORRECT' }),
      pinWrong: () => dispatch({ type: 'PIN_WRONG' }),
      pinSetupCompleted: () => {
        boot.setPinSet(true);
        dispatch({ type: 'PIN_SETUP_COMPLETED' });
      },
    }),
    [dispatch, boot],
  );

  return { state, actions };
};
